import React, { useState, useRef, useEffect } from "react";
import { Upload, FileText, X, Video } from "lucide-react";
import {
  AppButton, AppModal, Callout, DropZone, FormField, IconButton, Input, NativeSelect, ProgressBar, Switch, Textarea,
} from "../ui";
import apiClient from "../../utils/apiClient";
import { MATERIAL_MAX_BYTES, formatMaxSize, formatFileSize, validateFileSize } from "../../utils/uploadLimits";
import { useContentProtection } from "../../contexts/ContentProtectionContext";
import StudentAccessPicker from "../ContentProtection/StudentAccessPicker";
import { defaultAccessFor, fileTypeOf } from "../ContentProtection/contentAccess";

const ACCEPTED_TYPES = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.jpg,.jpeg,.png";
const ACCEPTED_TYPES_WITH_VIDEO = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.jpg,.jpeg,.png,.mp4,.mov,.webm,.mkv";
const VIDEO_EXTS = ["mp4", "mov", "webm", "mkv"];
const FORM_ID = "upload-material-form";

const extOf = (name = "") => name.split(".").pop().toLowerCase();

/**
 * UploadMaterialModal — the one dialog that uploads a material.
 *
 * Props:
 *   show, onClose
 *   onSuccess     – (material) after a successful upload
 *   batchId       – required for batch/video-level uploads
 *   courseId      – required for course-level uploads (admin only)
 *   videoId       – if provided, locks level to "video" for that video
 *   defaultLevel  – "video" | "batch" | "course" (default "batch")
 *   hideLevel     – hide the level selector (used when context is obvious)
 *   videosOnly    – restrict uploads to video files only (course Videos tab)
 *
 *   Uploading from a batch's syllabus — the file lands on the day (and topic)
 *   in the same request, checked by the server before anything is stored:
 *   syllabusDay   – the day number; forces a batch-level upload
 *   topicIndex    – 0-based topic of that day; omit for the whole day
 *   targetLabel   – "Day 6 · Python basics", shown so the trainer knows where
 *   defaultSessionDate – YYYY-MM-DD to start the Session date at
 *   defaultTopic  – to start the Topic field at
 */
const UploadMaterialModal = ({
  show, onClose, batchId, courseId, videoId,
  defaultLevel = "batch", hideLevel = false, videosOnly = false, onSuccess,
  syllabusDay = null, topicIndex = null, targetLabel = "", defaultSessionDate = "", defaultTopic = "",
}) => {
  const forSyllabus = syllabusDay != null;
  const startLevel = videoId ? "video" : forSyllabus ? "batch" : defaultLevel;

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState(defaultTopic);
  const [sessionDate, setSessionDate] = useState(defaultSessionDate);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [level, setLevel] = useState(startLevel);
  const [selectedVideoId, setSelectedVideoId] = useState(videoId || "");
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const { policy } = useContentProtection();
  const [studentAccess, setStudentAccess] = useState(() => defaultAccessFor("material", policy));
  const fileInputRef = useRef(null);

  // Start every upload at the institute's default (the policy may arrive after mount).
  useEffect(() => {
    if (show) setStudentAccess(defaultAccessFor("material", policy));
  }, [show, policy]);

  // Every opening starts from the caller's context.
  useEffect(() => {
    if (!show) return;
    setLevel(startLevel);
    setSelectedVideoId(videoId || "");
    setSessionDate(defaultSessionDate || "");
    setTopic(defaultTopic || "");
  }, [show, startLevel, videoId, defaultSessionDate, defaultTopic]);

  // Fetch batch videos when level is "video" and batchId is available
  useEffect(() => {
    if (show && level === "video" && batchId && !videoId) {
      apiClient.get(`/videos/batch/${batchId}`, { params: { page: 1, limit: 200 } })
        .then(({ data }) => { if (data.success) setVideos(data.data || []); })
        .catch(() => setVideos([]));
    }
  }, [show, level, batchId, videoId]);

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setTopic(defaultTopic || "");
    setSessionDate(defaultSessionDate || "");
    setDescription("");
    setCategory("other");
    setLevel(startLevel);
    setSelectedVideoId(videoId || "");
    setUploading(false);
    setUploadProgress(0);
    setError("");
    setDragActive(false);
    setIsPreview(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose();
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    const ext = extOf(selectedFile.name);
    if (videosOnly && !VIDEO_EXTS.includes(ext)) {
      setError("Please select a video file (MP4, MOV, WebM, MKV).");
      return;
    }
    const sizeError = validateFileSize(selectedFile, MATERIAL_MAX_BYTES);
    if (sizeError) {
      setError(sizeError);
      return;
    }
    setError("");
    setFile(selectedFile);

    if (!title) setTitle(selectedFile.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " "));

    if (["ppt", "pptx"].includes(ext)) setCategory("ppt");
    else if (["doc", "docx", "txt"].includes(ext)) setCategory("script");
    else if (ext === "pdf") setCategory("notes");
    else if (VIDEO_EXTS.includes(ext)) setCategory("other");
  };

  const isVideoFile = !!file && VIDEO_EXTS.includes(extOf(file.name));
  const isCourseLevel = level === "course";
  const acceptTypes = videosOnly
    ? VIDEO_EXTS.map((e) => `.${e}`).join(",")
    : isCourseLevel ? ACCEPTED_TYPES_WITH_VIDEO : ACCEPTED_TYPES;
  const acceptedHint = videosOnly
    ? "Videos only (MP4, MOV, WebM, MKV)"
    : isCourseLevel ? "PDF, PPT, DOC, XLS, TXT, ZIP, images, videos (MP4, MOV, WebM)" : "PDF, PPT, DOC, XLS, TXT, ZIP, images";

  const missing = !file || !title.trim()
    || (level === "batch" && !sessionDate)
    || (level === "video" && !selectedVideoId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      setError("Choose a file and give it a title.");
      return;
    }
    if (level === "batch" && !sessionDate) {
      setError("A batch material needs its session date.");
      return;
    }
    if (level === "video" && !selectedVideoId) {
      setError("Choose the video this material belongs to.");
      return;
    }

    setUploading(true);
    setError("");
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("level", level);
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      formData.append("topic", topic.trim());
      formData.append("category", category);
      formData.append("fileName", file.name);
      formData.append("fileSize", file.size);
      if (!isVideoFile) formData.append("studentAccess", studentAccess);

      if (level === "video") {
        formData.append("videoId", selectedVideoId);
        formData.append("batchId", batchId);
      } else if (level === "batch") {
        formData.append("batchId", batchId);
        formData.append("sessionDate", sessionDate);
        if (forSyllabus) {
          formData.append("syllabusDay", String(syllabusDay));
          if (topicIndex != null) formData.append("topicIndex", String(topicIndex));
        }
      } else if (level === "course") {
        formData.append("courseId", courseId);
        if (isVideoFile) formData.append("isPreview", isPreview);
      }
      if (sessionDate && level !== "batch") formData.append("sessionDate", sessionDate);

      const { data: uploadData } = await apiClient.post("/materials/upload", formData, {
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });

      if (!uploadData.success) {
        setError(uploadData.message || "The upload failed.");
        setUploading(false);
        return;
      }

      resetForm();
      onClose();
      if (onSuccess) onSuccess(uploadData.data?.material);
    } catch (err) {
      setError(err.response?.data?.message || "The upload failed. Please try again.");
      setUploading(false);
    }
  };

  const heading = videosOnly ? "Upload course video"
    : level === "video" ? "Upload video material"
      : level === "course" ? "Upload course material"
        : "Upload class material";

  return (
    <AppModal
      show={show}
      onHide={uploading ? undefined : handleClose}
      hideCloseButton={uploading}
      size="md"
      title={heading}
      icon={<Upload size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={handleClose} disabled={uploading}>Cancel</AppButton>
          <AppButton type="submit" form={FORM_ID} disabled={uploading || missing}>
            {uploading ? `Uploading… ${uploadProgress}%` : "Upload"}
          </AppButton>
        </div>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="tw-flex tw-flex-col tw-gap-4">
        {targetLabel && (
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            For <span className="tw-font-semibold tw-text-content">{targetLabel}</span>
          </p>
        )}

        {!hideLevel && !forSyllabus && (
          <FormField label="Material level">
            <NativeSelect
              value={level}
              onChange={(e) => { setLevel(e.target.value); setSelectedVideoId(""); setError(""); }}
              disabled={uploading || !!videoId}
            >
              <option value="batch">Batch — for this batch only</option>
              <option value="video">Video — attached to a recording</option>
              {courseId && <option value="course">Course — shared across all batches</option>}
            </NativeSelect>
          </FormField>
        )}

        {level === "video" && !videoId && (
          <FormField label="Attach to video" required>
            <NativeSelect value={selectedVideoId} onChange={(e) => setSelectedVideoId(e.target.value)} disabled={uploading}>
              <option value="">Select a video…</option>
              {videos.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.title}{v.recordedAt ? ` (${new Date(v.recordedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })})` : ""}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes}
          className="tw-hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => { handleFileChange(e.target.files?.[0]); e.target.value = ""; }}
        />
        {file ? (
          <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-px-3.5 tw-py-2.5">
            {isVideoFile
              ? <Video size={18} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
              : <FileText size={18} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />}
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
              <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{file.name}</span>
              <span className="tw-text-xs tw-text-content-muted">{formatFileSize(file.size)}{isVideoFile && " · Video"}</span>
            </span>
            {!uploading && <IconButton label="Remove file" icon={X} size="sm" onClick={() => setFile(null)} />}
          </div>
        ) : (
          <DropZone
            state={dragActive ? "active" : "idle"}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFileChange(e.dataTransfer.files?.[0]); }}
          >
            <Upload size={20} aria-hidden="true" className="tw-text-content-subtle" />
            <span className="tw-text-sm tw-font-medium tw-text-content">Drop a file here or click to browse</span>
            <span className="tw-text-xs tw-text-content-subtle">{acceptedHint} · Max {formatMaxSize(MATERIAL_MAX_BYTES)}</span>
          </DropZone>
        )}

        {uploading && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <ProgressBar value={uploadProgress} color="primary" />
            <span className="tw-text-xs tw-text-content-muted">{uploadProgress}% uploaded</span>
          </div>
        )}

        <FormField label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isVideoFile ? "e.g. Course introduction" : "e.g. Day 5 — React Hooks PPT"}
            disabled={uploading}
          />
        </FormField>

        {isCourseLevel && isVideoFile && (
          <Switch
            checked={isPreview}
            onChange={setIsPreview}
            disabled={uploading}
            label="Free preview"
            description="Unenrolled visitors may watch this video before buying the course."
          />
        )}

        <div className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-2">
          <FormField label="Category">
            <NativeSelect value={category} onChange={(e) => setCategory(e.target.value)} disabled={uploading}>
              <option value="ppt">PPT / Presentation</option>
              <option value="script">Script</option>
              <option value="notes">Notes</option>
              <option value="other">Other</option>
            </NativeSelect>
          </FormField>
          {level === "batch" && (
            <FormField label="Session date" required>
              <Input type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} disabled={uploading} />
            </FormField>
          )}
        </div>

        <FormField label="Topic">
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. React Hooks" disabled={uploading} />
        </FormField>

        <FormField label="Description">
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" disabled={uploading} />
        </FormField>

        {!videosOnly && (
          <StudentAccessPicker
            value={studentAccess}
            onChange={setStudentAccess}
            fileType={file ? fileTypeOf(file) : ""}
            isVideo={isVideoFile}
            disabled={uploading}
          />
        )}

        {error && <Callout tone="danger" role="alert">{error}</Callout>}
      </form>
    </AppModal>
  );
};

export default UploadMaterialModal;
